//used for local component isolation from sentry

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { FallbackUI } from './FallbackUI';
import { UpdatingNotice } from './UpdatingNotice';
import {
  isStaleChunkError,
  reloadOnceForStaleChunk,
} from '@/shared/utils/staleChunk';

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
  isStaleChunk: boolean;
  recoveryFailed: boolean;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: undefined,
    isStaleChunk: false,
    recoveryFailed: false,
  };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      isStaleChunk: isStaleChunkError(error),
      recoveryFailed: false,
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A stale chunk means the tab is running a bundle the origin no longer
    // serves (SP-1330). Reload once instead of stranding the user on the
    // fallback; if the guard refuses, fall through to the normal fallback.
    if (isStaleChunkError(error)) {
      console.warn('Stale chunk detected, reloading:', error);
      if (!reloadOnceForStaleChunk()) {
        this.setState({ recoveryFailed: true });
      }
      return;
    }

    console.error('Render error:', error);
    console.error('Component stack:', info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isStaleChunk && !this.state.recoveryFailed) {
        return <UpdatingNotice />;
      }
      return <FallbackUI error={this.state.error} />;
    }
    return this.props.children;
  }
}
